import { SitePartOperation } from "../types.ts";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { autoCompleteSingleSelectColumn, floatColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { concatWithPipe, isNullOrBlank } from "src/utils/strings.tsx";
import { GridBooleanCell } from "@mui/x-data-grid-pro";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { faArrowDownToLine, faLaptopCode, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { FormConfiguratorLuaEditor } from "src/components/views/erp/configurator/scripting/FormConfiguratorLuaEditor.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useStaticDataGridModel } from "src/components/common/dataGrid/gridModel/useStaticDataGridModel.tsx";
import { Operation } from "src/api/generated/erp/db/types/tables/operation.ts";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { FormLazySelectField } from "src/components/common/forms/fields/FormLazySelectField.tsx";
import { SitePartQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartQueryApi.ts";
import { SitePartRevisionQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartRevisionQueryApi.ts";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { PartRevisionView } from "src/api/generated/erp/db/types/tables/partRevisionView.ts";
import { requireRule } from "src/components/common/forms/validation.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { getPartRevisionStateLabel } from "src/api/generated/erp/db/types/enums/partRevisionState.ts";
import { isValidSitePartOperations } from "src/components/views/erp/parts/catalogPart/partCreationWizard/validation.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

interface SitePartOperationsStepProps {
	operationOptions: Operation[];
	sitePartOperations: SitePartOperation[];
	onUpdateSitePartOperations: (operations: SitePartOperation[]) => void;
	partIsConfigurable: boolean;
	templateSitePartId: number | undefined;
	templateCatalogPartRevisionId: number | undefined;
	templateSitePartRevisionId: number | undefined;
	onSelectTemplateRevision: (templateSitePartRevisionId: number) => void;
	onValidationChange?: (isValid: boolean) => void;
}

export const SitePartOperationsStep = ({
	sitePartOperations,
	onUpdateSitePartOperations,
	operationOptions,
	partIsConfigurable,
	templateSitePartId,
	templateCatalogPartRevisionId,
	templateSitePartRevisionId,
	onSelectTemplateRevision,
	onValidationChange,
}: SitePartOperationsStepProps) => {
	const showFormDialog = useFormInput();
	const { defaultSiteId } = useGlobalInitData();

	const { dataGridProps, selectedRows } = useStaticDataGridModel<SitePartOperation>({
		gridId: "site-part-operations-step",
		rows: sitePartOperations,
		getRowId: (row: SitePartOperation) => row.rowId,
	});

	const operationOptionsByIds = operationOptions.reduce(
		(acc, op) => {
			acc[op.operationId] = op;
			return acc;
		},
		{} as Record<number, Operation>,
	);

	return (
		<AavoDataGrid<SitePartOperation>
			columns={[
				autoCompleteSingleSelectColumn<SitePartOperation, Operation, number, true>({
					field: "operationId",
					headerName: i18n.t("operation"),
					editable: true,
					width: 200,
					validate: "required",
					disableClearable: true,
					valueOptions: ({ id }) => {
						const usedOperationIds = sitePartOperations
							.filter((op) => {
								if (op.operationId == null) return false;
								// Allow use operation of current row
								return id !== op.rowId;
							})
							.map((op) => op.operationId);

						return operationOptions.filter((op) => !usedOperationIds.includes(op.operationId));
					},
					getOptionValue: (op) => op.operationId,
					getOptionLabel: (op) => concatWithPipe(op.operationNo, op.operationDescription),
					onChange: ({ row, option }) => {
						let updatedRow = { ...row, operationId: option.operationId };
						// Set material operation to selected operation if not set
						if (row.materialOperationRowId == null) {
							updatedRow = { ...updatedRow, materialOperationRowId: row.rowId };
						}
						upsertRow(updatedRow);
					},
				}),
				autoCompleteSingleSelectColumn<SitePartOperation, SitePartOperation, string, false>({
					field: "materialOperationRowId",
					headerName: i18n.t("material_operation"),
					editable: true,
					width: 200,
					validate: "required",
					valueOptions: ({ id }) =>
						sitePartOperations
							.filter((op) => op.operationId != null)
							// Show self row first.
							.toSorted((a, b) => {
								if (a.rowId === id) return -1;
								if (b.rowId === id) return 1;
								return 0;
							}),
					getOptionValue: ({ rowId }) => rowId,
					getOptionLabel: ({ operationId }) => {
						const operation = operationOptionsByIds[operationId!];
						if (operation == null) return "";
						return concatWithPipe(operation.operationNo, operation.operationDescription);
					},
				}),
				floatColumn({
					field: "setUpTime",
					headerName: i18n.t("set_up_time_hours"),
					editable: true,
					width: 150,
				}),
				floatColumn({
					field: "workingTime",
					headerName: i18n.t("working_time_hours"),
					editable: true,
					width: 150,
				}),
				partIsConfigurable && {
					field: "hasConfiguratorScript",
					type: "boolean",
					headerName: i18n.t("configuration_rule"),
					width: 150,
					valueGetter: (_, row) => !isNullOrBlank(row.configuratorTransformationScript),
					renderCell: (params) => (
						<HorizontalBox sx={{ alignItems: "center", gap: 0.5 }}>
							<GridBooleanCell {...params} />
							<AsyncButton
								icon={faLaptopCode}
								size={"small"}
								tooltip={i18n.t("configuration_rule")}
								onClick={async () => {
									await openTransformationScriptEditor(params.row);
								}}
							/>
						</HorizontalBox>
					),
				},
			]}
			processRowUpdate={(newRow, oldRow) => {
				upsertRow({ ...newRow, rowId: oldRow.rowId });
				return newRow;
			}}
			editMode={"row"}
			actionBarComponents={
				<>
					<AsyncButton icon={faPlus} tooltip={i18n.t("add_new")} onClick={handleAdd} />
					<AsyncButton icon={faTrash} tooltip={i18n.t("remove")} onClick={handleRemove} />
					<AsyncButton
						label={i18n.t("import_from_template")}
						icon={faArrowDownToLine}
						onClick={handleImportFromTemplate}
						sx={{
							alignSelf: "flex-start",
						}}
					/>
				</>
			}
			agentContext={{
				key: defineAgentUiContextKey("erp.parts.partCreationWizardSitePartOperations.grid"),
				data: {
					templateSitePartId: templateSitePartId ?? null,
					templateSitePartRevisionId: templateSitePartRevisionId ?? null,
				},
				rowFields: ["rowId", "operationId", "materialOperationRowId"],
				selectedRowPathSegment: (row) => {
					const operation = operationOptionsByIds[row.operationId];
					return operation == null ?
							i18n.t("operation")
						:	concatWithPipe(operation.operationNo, operation.operationDescription);
				},
			}}
			{...dataGridProps}
		/>
	);

	async function openTransformationScriptEditor(row: SitePartOperation) {
		const formResult = await showFormDialog<{ script: string }>({
			title: i18n.t("configuration_rule"),
			size: "fullscreen",
			defaultValues: {
				script: row.configuratorTransformationScript || "",
			},
			content: ({ control }) => (
				<FormConfiguratorLuaEditor
					control={control}
					name={"script"}
					productFamilyVersionId={null} // In wizard, we don't have product family version yet
					catalogPartRevisionId={null} // In wizard, we don't have catalog part revision yet
				/>
			),
		});
		if (formResult === undefined) return;

		upsertRow({ ...row, configuratorTransformationScript: formResult.script });
	}

	function handleAdd() {
		const rowId = crypto.randomUUID();
		const newOperation: Partial<SitePartOperation> = {
			rowId: rowId,
			materialOperationRowId: rowId,
			setUpTime: undefined,
			workingTime: undefined,
			configuratorTransformationScript: "",
		};
		upsertRow(newOperation as SitePartOperation);
	}

	function handleRemove() {
		const idsToRemove = new Set(selectedRows.map((row: SitePartOperation) => row.rowId));
		const updatedOperations = sitePartOperations.filter((operation) => !idsToRemove.has(operation.rowId));
		onUpdateSitePartOperations(updatedOperations);
		triggerValidation(updatedOperations);
	}

	function upsertRow(row: SitePartOperation) {
		const existingIndex = sitePartOperations.findIndex((operation) => operation.rowId === row.rowId);
		const updatedOperations =
			existingIndex === -1 ?
				[...sitePartOperations, row]
			:	sitePartOperations.map((operation, index) => (index === existingIndex ? row : operation));

		onUpdateSitePartOperations(updatedOperations);
		triggerValidation(updatedOperations);
	}

	async function handleImportFromTemplate() {
		const defaultSitePartRevisionId =
			templateSitePartRevisionId != null ? templateSitePartRevisionId
			: templateCatalogPartRevisionId != null && templateSitePartId != null ?
				await SitePartRevisionQueryApi.getSitePartRevisionIdForCatalogPartRevision({
					partId: templateSitePartId,
					catalogPartRevisionId: templateCatalogPartRevisionId,
				})
			:	undefined;

		const formResult = await showFormDialog<{
			sitePartId: number;
			sitePartRevisionId: number;
		}>({
			title: i18n.t("import_from_template"),
			submitLabel: i18n.t("import"),
			submitOnEnter: true,
			defaultValues: {
				sitePartId: templateSitePartId,
				sitePartRevisionId: defaultSitePartRevisionId ?? undefined,
			},
			content: ({ control, watch, setValue }) => {
				const selectedSitePartId = watch("sitePartId");

				return (
					<>
						<FormAsyncSelectField
							control={control}
							name={"sitePartId"}
							label={i18n.t("part")}
							fetchOptions={async ({ searchQuery, currentSelection }) =>
								SitePartQueryApi.getPartSelectionOptions({
									searchQuery: searchQuery,
									currentPartId: currentSelection,
									siteId: defaultSiteId,
								})
							}
							getOptionKey={(option: PartView) => option.partId}
							getOptionLabel={(option) =>
								concatWithPipe(option.partNo, option.partDescription_1, option.partDescription_2)
							}
							rules={requireRule()}
							onChange={() => {
								setValue("sitePartRevisionId", null);
							}}
						/>
						<FormLazySelectField
							control={control}
							name={"sitePartRevisionId"}
							label={i18n.t("revision")}
							fetchOptions={async () => {
								if (!selectedSitePartId) return [];
								return await SitePartRevisionQueryApi.getPartRevisionSelectionOptions({
									partId: selectedSitePartId,
								});
							}}
							getOptionKey={(option: PartRevisionView) => option.partRevisionId}
							getOptionLabel={(option) =>
								concatWithPipe(
									option.revisionNumber,
									getPartRevisionStateLabel(option.partRevisionState),
									option.note,
								)
							}
							rules={requireRule()}
						/>
					</>
				);
			},
		});

		if (formResult === undefined) return;

		const templateOperations = await SitePartRevisionQueryApi.getPartRevisionOperations({
			partRevisionId: formResult.sitePartRevisionId,
		});

		const importedOperations: SitePartOperation[] = templateOperations.map((templateOp) => ({
			...templateOp,
			rowId: templateOp.partRevisionOperationId.toString(),
			materialOperationRowId:
				templateOp.materialOperationId?.toString() ?? templateOp.partRevisionOperationId.toString(),
			setUpTime: templateOp.setUpTime ?? undefined,
			workingTime: templateOp.workingTime ?? undefined,
		}));

		onUpdateSitePartOperations(importedOperations);
		onSelectTemplateRevision(formResult.sitePartRevisionId);
		triggerValidation(importedOperations);
	}

	function triggerValidation(operations: SitePartOperation[]) {
		onValidationChange?.(isValidSitePartOperations(operations));
	}
};
